/**
 * voice-v8 — Werkzeugkarten: Kontakte, Anruf, Verzeichnis.
 *
 * Herausgelöst aus lib/tool-cards.ts (15.10.2026, Plan „Karten zu 100 % einheitlich“, Welle 3) — Verhalten unverändert;
 * der eine Einstieg bleibt `buildToolCard` in lib/tool-cards.ts.
 */
import { aktionsKnopf, aktionsLeiste, baueKarte, hinweisKarte, kartenTitel, kuerzeListe, linkZeile, liste, listenEintrag, weitereHinweis, wertZeile } from "../karte.js";
import { unwrapResult } from "./hilfe.js";

interface CallResult {
  name?: string;
  number?: string;
  via?: "phone" | "whatsapp" | "telegram";
  link?: string;
}

export function buildCallCard(result: unknown): HTMLElement {
  const r = (result ?? {}) as CallResult;
  const el = baueKarte("call");
  el.appendChild(kartenTitel("Anrufen"));
  wertZeile(el, "Name", r.name ?? "?");
  wertZeile(el, "Nummer", r.number ?? "?");

  if (r.link) {
    aktionsLeiste(el).appendChild(
      aktionsKnopf({
        text: r.via === "whatsapp" ? "WhatsApp öffnen" : r.via === "telegram" ? "Telegram öffnen" : "Anruf starten",
        primaer: true,
        href: r.link,
        // Ein Telefonlink bleibt im Fenster; WhatsApp und Telegram öffnen draußen.
        neuesFenster: r.via !== "phone",
      }),
    );
  }
  return el;
}

// --- Contact-Tools (Pre.135) ---------------------------------------------
//
// Subagent B hat die contact_*-Tools registriert, aber tool-cards.ts hatte
// keine Render-Branches → Default-Generic-Card schüttete Roh-JSON in den
// Chat (Live-Bug Martin: "{\"ok\":true,\"contact\":{\"id\":\"...\"...}}").

const CATEGORY_LABELS: Record<string, string> = {
  familie: "Familie",
  freunde: "Freunde",
  arbeit: "Arbeit",
  dienste: "Dienste",
  notfall: "Notfall",
  allgemein: "Allgemein",
};

interface ContactItem {
  id?: string;
  name?: string;
  phone?: string | null;
  email?: string | null;
  company?: string | null;
  category?: string;
}

// Pre.969: DRY tappbarer Anruf-Knopf (tel: → Wähler/Anruf). Liefert null, wenn
// keine Nummer da ist. `target=_self` wie buildCallCard für den Telefon-Pfad.
function contactCallButton(phone: unknown): HTMLAnchorElement | null {
  const tel = typeof phone === "string" ? phone.replace(/[^\d+]/g, "") : "";
  if (!tel) return null;
  const a = document.createElement("a");
  // 2026-07-04 (Martin): eigene Pillen-Klasse — der 52px-Kreis der
  // Kontakte-Ansicht liess den Text in der Chat-Karte auslaufen.
  a.className = "tool-call-pill";
  a.href = `tel:${tel}`;
  a.target = "_self";
  a.rel = "noopener";
  a.textContent = "Anrufen";
  return a;
}

// 2026-07-03 (Martin, Ticket 225): einheitliche Kontakt-Detail-Darstellung —
// Kategorie als Badge, gruppierte Felder, Website als Link, Anruf-Knopf.
function renderContactDetail(el: HTMLElement, c: ContactItem): void {
  if (c.category) {
    const badges = document.createElement("div");
    badges.className = "tool-ticket-badges";
    const b = document.createElement("span");
    b.className = "tickets-badge";
    b.textContent = CATEGORY_LABELS[c.category] ?? c.category;
    badges.appendChild(b);
    el.appendChild(badges);
  }
  if (c.phone) wertZeile(el, "Telefon", c.phone);
  if (c.email) wertZeile(el, "E-Mail", c.email);
  if (c.company) wertZeile(el, "Firma", c.company);
  const hours = (c as { opening_hours?: string | null }).opening_hours;
  if (hours) wertZeile(el, "\u00d6ffnungszeiten", hours);
  const website = (c as { website?: string | null }).website;
  if (website) el.appendChild(linkZeile(website, "Website"));
  const callBtn = contactCallButton(c.phone ?? null);
  if (callBtn) el.appendChild(callBtn);
}

/**
 * Treffer aus der Verzeichnis-Suche — Firmen und Personen aus dem Netz.
 *
 * Martin, 25.08. (Entwurf umgesetzt): Auf die Frage nach einer Telefonnummer
 * kam bis jetzt Fliesstext ("Basaltwerk Pauliberg / Telefon: 02618 8202 /
 * Heute geöffnet bis 16:00"). Die Nummer war zwar anklickbar (markdown.ts
 * verlinkt sie), aber man musste sie im Satz suchen.
 *
 * `research_directory` liefert bereits Struktur — {name, ort, telefon, mail,
 * spezial, quelle_url} —, es fehlte nur die Karte dazu. Sie nutzt dieselben
 * Bausteine wie die gespeicherten Kontakte (card/header/listItem/
 * contactCallButton), damit ein Treffer aus dem Netz und einer aus dem
 * Adressbuch gleich aussehen.
 */
interface VerzeichnisEintrag {
  name?: string;
  ort?: string;
  telefon?: string | null;
  mail?: string | null;
  spezial?: string | null;
  quelle_url?: string;
  quelle_titel?: string;
}

export function buildVerzeichnisCard(result: unknown): HTMLElement {
  const r = unwrapResult(result);
  const el = baueKarte("research-directory");
  const eintraege = Array.isArray(r.entries)
    ? (r.entries as VerzeichnisEintrag[])
    : [];

  if (eintraege.length === 0) {
    el.appendChild(kartenTitel("Verzeichnis"));
    const leer = document.createElement("div");
    leer.className = "tool-browser-detail";
    leer.textContent = "Keine Einträge gefunden.";
    el.appendChild(leer);
    return el;
  }

  // Ein Treffer ist der Normalfall bei "gib mir die Nummer von X" — dann
  // gehört er nicht in eine Liste, sondern gross auf die Karte.
  if (eintraege.length === 1) {
    const e = eintraege[0];
    el.appendChild(kartenTitel(e.name?.trim() || "Treffer"));
    verzeichnisDetail(el, e);
    return el;
  }

  el.appendChild(kartenTitel(`Verzeichnis · ${eintraege.length}`));
  const ul = liste(el);
  const gekuerzt = kuerzeListe(eintraege);
  for (const e of gekuerzt.sichtbar) {
    const neben = [e.ort, e.spezial].filter(Boolean).join(" · ");
    const li = listenEintrag(e.name?.trim() || "(ohne Namen)", neben || undefined);
    const anruf = contactCallButton(e.telefon);
    if (anruf) li.appendChild(anruf);
    ul.appendChild(li);
  }
  weitereHinweis(el, gekuerzt.rest);
  return el;
}

/** Ein einzelner Treffer, gross: Ort, Nummer zum Antippen, Mail, Quelle. */
function verzeichnisDetail(el: HTMLElement, e: VerzeichnisEintrag): void {
  const ort = e.ort?.trim();
  const spezial = e.spezial?.trim();
  const unterzeile = [ort, spezial].filter(Boolean).join(" · ");
  if (unterzeile) {
    const sub = document.createElement("div");
    sub.className = "tool-browser-detail";
    sub.textContent = unterzeile;
    el.appendChild(sub);
  }

  const tel = e.telefon?.trim();
  if (tel) {
    const zeile = document.createElement("div");
    zeile.className = "tool-call-phone";
    zeile.textContent = tel;
    el.appendChild(zeile);
    const anruf = contactCallButton(tel);
    if (anruf) el.appendChild(anruf);
  }

  const mail = e.mail?.trim();
  if (mail) {
    const a = document.createElement("a");
    a.className = "tool-browser-detail";
    a.href = `mailto:${mail}`;
    a.rel = "noopener";
    a.textContent = mail;
    el.appendChild(a);
  }

  const url = e.quelle_url?.trim();
  if (url && /^https?:\/\//.test(url)) {
    const q = document.createElement("a");
    q.className = "tool-browser-detail";
    q.href = url;
    q.target = "_blank";
    q.rel = "noopener noreferrer";
    q.textContent = e.quelle_titel?.trim() || "Quelle";
    el.appendChild(q);
  }
}

export function buildContactCard(tool: string, result: unknown): HTMLElement {
  const r = unwrapResult(result);
  const el = baueKarte(tool.replace(/_/g, "-"));

  if (tool === "contact_list" || tool === "contact_find") {
    const items = (r.items as ContactItem[] | undefined) ?? [];
    const total = typeof r.total === "number" ? r.total : items.length;
    // 2026-07-03 (Martin, #225): EIN Treffer -> reiche Detail-Karte statt
    // magerer Ein-Zeilen-Liste.
    if (tool === "contact_find" && items.length === 1) {
      const c0 = items[0];
      el.appendChild(kartenTitel(c0.name ?? "Kontakt"));
      renderContactDetail(el, c0);
      return el;
    }
    el.appendChild(
      kartenTitel(
        tool === "contact_find"
          ? `Treffer · ${items.length}`
          : `Kontakte · ${total}`,
      ),
    );
    if (items.length === 0) {
      const empty = document.createElement("div");
      empty.className = "tool-browser-detail";
      empty.textContent = "Keine Kontakte gefunden.";
      el.appendChild(empty);
      return el;
    }
    const ul = liste(el);
    const gekuerzt = kuerzeListe(items);
    for (const c of gekuerzt.sichtbar) {
      const sub = [
        c.phone,
        c.company,
        c.category ? CATEGORY_LABELS[c.category] : undefined,
      ]
        .filter(Boolean)
        .join(" · ");
      const li = listenEintrag(c.name ?? "(unbenannt)", sub || undefined);
      // Pre.969: tappbarer Anruf-Knopf direkt am Treffer.
      const callBtn = contactCallButton(c.phone);
      if (callBtn) li.appendChild(callBtn);
      ul.appendChild(li);
    }
    weitereHinweis(el, gekuerzt.rest);
    return el;
  }

  if (tool === "contact_call") {
    /*
     * 05.10.2026 (Ticket 3002): lehnt das Werkzeug ab, zeigt die Karte seinen
     * Satz. Bis dahin las sie `error` nicht — heraus kam „Anrufen · Kontakt“
     * ohne Knopf: ein behaupteter Anruf, den es nicht gab, und kein Wort
     * dazu, warum. Seit der Rufnummern-Sperre trifft das jeden Kontakt mit
     * Notruf- oder Sondernummer („Notrufe wähle ich nicht – ruf bitte selbst
     * die 144 an.“). „ambiguous“ ist ein Wort für das Modell, kein Satz.
     */
    if (r.ok === false) {
      const satz = typeof r.error === "string" ? r.error.trim() : "";
      return hinweisKarte(
        satz === "ambiguous"
          ? "Mehrere Kontakte passen – welchen meinst du?"
          : satz || "Der Anruf wurde nicht vorbereitet.",
      );
    }
    const phone = typeof r.phone === "string" ? r.phone : "";
    const name = typeof r.name === "string" ? r.name : "Kontakt";
    el.appendChild(kartenTitel("Anrufen"));
    const who = document.createElement("div");
    who.className = "tool-call-name";
    who.textContent = name;
    el.appendChild(who);
    if (phone) {
      const ph = document.createElement("div");
      ph.className = "tool-call-phone";
      ph.textContent = phone;
      el.appendChild(ph);
    }
    const callBtn = contactCallButton(phone);
    if (callBtn) {
      callBtn.classList.add("tool-call-pill--wide");
      el.appendChild(callBtn);
    }
    return el;
  }

  // contact_create / contact_update / contact_delete
  const c = (r.contact as ContactItem | undefined) ?? {};
  const labels: Record<string, string> = {
    contact_create: "Kontakt angelegt",
    contact_update: "Kontakt aktualisiert",
    contact_delete: "Kontakt gelöscht",
  };
  const cname = c.name ? `: ${c.name}` : "";
  el.appendChild(kartenTitel(`${labels[tool] ?? tool}${cname}`));
  renderContactDetail(el, c);
  if (tool === "contact_delete" && r.deleted) {
    wertZeile(el, "Status", "gelöscht");
  }
  return el;
}
